import React, { useState, useEffect } from 'react';
import { Card } from 'react-bootstrap';

import { api, MasterTableItem } from '../../../helpers';
import { useApi } from '../../../hooks/useApi';

import Container from '../../common/Container';

import './style.css';

export interface OverviewData {
    image?: string;
    latest_update?: string;
    total?: number;
}

export interface OverviewProps {
    overviewData?: OverviewData;
    autoFetch?: boolean;
}

const OverviewPlaceholder: React.FC = () => {
    return (
        <Container>
            <div className='row'>
                <div className='col-12'>
                    <Card className='border-0 shadow-sm rounded-4 overflow-hidden position-relative' style={{ height: '520px' }}>
                        {/* Background placeholder with orange gradient overlay */}
                        <div 
                            className='position-absolute top-0 start-0 w-100 h-100'
                            style={{
                                background: 'linear-gradient(90deg, #FF8C42 0%, #FF6B35 50%, #E55A2B 100%)',
                                zIndex: 1
                            }}
                        >
                            <div className='position-absolute h-100 w-100 d-flex align-items-center justify-content-center'>
                                <div className='spinner-border text-white' role='status'>
                                    <span className='visually-hidden'>Loading...</span>
                                </div>
                            </div>
                        </div>
                        
                        {/* Purple overlay placeholder for text */}
                        <div 
                            className='position-absolute bottom-0 end-0'
                            style={{
                                background: 'linear-gradient(90deg, #6B46C1 0%, #4C1D95 50%, #1F2937 100%)',
                                width: '65%',
                                height: '200px',
                                borderTopLeftRadius: '20px',
                                zIndex: 2,
                                padding: '30px'
                            }}
                        >
                            <div className='text-white h-100 d-flex flex justify-content-between align-items-center'>
                                <div className='mb-2'>
                                    <div className='placeholder-glow'>
                                        <div className='placeholder bg-white bg-opacity-75 rounded mb-2' style={{ height: '48px', width: '300px' }}></div>
                                    </div>
                                </div>
                                <div className='mb-2'>
                                    <div className='d-flex align-items-center gap-4 mb-4'>
                                        <div className='placeholder-glow'>
                                            <div className='placeholder bg-white bg-opacity-75 rounded' style={{ height: '48px', width: '100px' }}></div>
                                        </div>
                                        <div className='placeholder-glow'>
                                            <div className='placeholder bg-primary rounded' style={{ height: '48px', width: '120px' }}></div>
                                        </div>
                                    </div>
                                    <div className='placeholder-glow'>
                                        <div className='placeholder bg-white bg-opacity-75 rounded' style={{ height: '32px', width: '200px' }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </Container>
    )
}

const Overview: React.FC<OverviewProps> = ({
    overviewData: propOverviewData = null,
    autoFetch = true
}) => {
    const [overview, setOverview] = useState<OverviewData | null>(propOverviewData);

    // Existing useApi for image overview state
    const { data, loading, error, execute: fetchOverviewData } = useApi(api.getMasterTable.bind(api));
    
    // New useApi for the additional overview data
    const { data: additionalData, loading: additionalLoading, error: additionalError, execute: fetchAdditionalData } = useApi(api.getOverviewData.bind(api));

    useEffect(() => {
        
        if (autoFetch && !overview) {
            fetchOverviewData({tipe: 'header_5'});
            fetchAdditionalData(); // Fetch additional data
        } else if (propOverviewData) {
            setOverview(propOverviewData);
        }
    }, [autoFetch, propOverviewData]);

    // Handle the existing overview data (for images)
    useEffect(() => {
        if (data?.data?.data && Array.isArray(data.data.data)) {
            const filteredItem = data.data.data.filter((item: MasterTableItem) => 
                item.is_active === 1 || item.is_active === '1'
            );
            if (filteredItem && filteredItem.length > 0) {
                setOverview(prev => ({ 
                    ...prev, 
                    image: filteredItem[0].image 
                }));
            }
        }
    }, [data]);

    // Handle the additional overview data (for total and latest update)
    useEffect(() => {
        if (additionalData) {
            const pageSetting = additionalData.data[0]['page-setting'];
            
            setOverview(prev => ({
                ...prev,
                // Set total from section_about_alumni
                total: pageSetting.section_about_alumni ||  prev?.total,
                // Set latest update from section_about_tanggal_update
                latest_update: pageSetting.section_about_tanggal_update || prev?.latest_update
            }));
        }
    }, [additionalData]);

    // just testing placeholder
    // return <OverviewPlaceholder />;

    // Show placeholder while loading
    if (loading || additionalLoading) {
        return <OverviewPlaceholder />;
    }

    return (
        <Container>
            <div className='row'>
                <div className='col-12'>
                    <Card className='border-0 shadow-sm rounded-4 overflow-hidden position-relative' style={{ height: '520px' }}>
                        {/* Background image with orange gradient overlay */}
                        <div 
                            className='position-absolute top-0 start-0 w-100 h-100'
                            style={{
                                background: 'linear-gradient(90deg, #FF8C42 0%, #FF6B35 50%, #E55A2B 100%)',
                                zIndex: 1
                            }}
                        >
                            <img 
                                src={overview?.image} 
                                alt="Alumni LPDP"
                                className='position-absolute'
                                style={{ 
                                objectFit: 'contain',
                                height: 'auto',
                                width: '818px',
                                left: '0%',
                                bottom: '0'
                                }}
                            />
                        </div>
                        
                        {/* Purple overlay for text */}
                        <div className='position-absolute bottom-0 end-0 card-overlay'>
                            <div className='text-white h-100 d-flex flex-column flex-md-row justify-content-between align-items-md-center align-items-end'>
                                <div className='mb-2'>
                                    <h4 className='fw-bold mb-0 display-4' style={{ textTransform: 'unset' }}>Total Alumni LPDP</h4>
                                </div>
                                <div className='mb-2'>
                                    <div className='d-flex align-items-md-center align-items-end gap-4'>
                                        <span className='display-3 text-white'>Total</span>
                                        <h2 className='display-3 fw-bold mb-0 text-primary'>{overview?.total}</h2>
                                    </div>
                                    <p className='fs-1 opacity-75 mb-0 text-end text-md-start'>Per {overview?.latest_update}</p>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </Container>
    )
}

export default Overview;